"use client";

import { c as _c } from "react/compiler-runtime";
import { AlertTriangle, ArrowRight, CheckCircle2, FileSpreadsheet, HelpCircle, MessageSquare, Phone, Search, Send, ShieldCheck, TrendingDown, TrendingUp, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { FloatingIcons } from "./floating-icons";

/* ------------------------------------------------------------------ */
/*  Before — animated chaos scene (pure CSS + React)                   */
/* ------------------------------------------------------------------ */
function ChaosScene() {
  const $ = _c(13);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="animate-float-slow absolute top-6 left-6 rotate-[-8deg]"><ExcelFile label=".xlsx" variant="gray" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="animate-float-medium absolute top-4 right-10 rotate-12"><ExcelFile label=".xlsx" variant="gray" /></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="animate-float-fast absolute bottom-16 left-1/4 rotate-[-4deg]"><ExcelFile label=".xlsx" variant="gray" /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="animate-float-slow absolute right-6 bottom-20 rotate-6"><ExcelFile label=".xls" variant="gray" /></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="animate-float-medium absolute top-14 left-1/3 rotate-3"><TelegramMsg /></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="animate-float-fast absolute right-1/4 bottom-10 rotate-[-5deg]"><TelegramMsg /></div>;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="animate-float-fast absolute top-1/3 right-8"><div className="flex size-10 items-center justify-center rounded-xl bg-slate-200 shadow-sm"><Phone className="size-4 text-slate-400" /></div></div>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="animate-float-medium absolute top-3 left-1/2 text-slate-300"><HelpCircle className="size-6" /></div>;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="animate-float-slow absolute right-4 bottom-6 text-slate-300"><HelpCircle className="size-5" /></div>;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="animate-float-fast absolute bottom-4 left-8 text-rose-300"><AlertTriangle className="size-5" /></div>;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="animate-float-medium absolute bottom-14 left-1/2 text-rose-300"><TrendingUp className="size-5" /></div>;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="animate-float-slow absolute top-10 right-1/3 text-slate-300"><TrendingDown className="size-5" /></div>;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="relative h-44 overflow-hidden rounded-xl bg-slate-50 sm:h-64">{t0}{t1}{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t11}<div className="absolute inset-0 flex items-center justify-center"><div className="rounded-full bg-white/80 px-4 py-2 text-xs font-medium text-slate-500 shadow-sm backdrop-blur-sm">Telegram + Excel + Telefon = ?</div></div></div>;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  return t12;
}

/* ------------------------------------------------------------------ */
/*  After — clean organized scene                                      */
/* ------------------------------------------------------------------ */
function OrderScene() {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex items-center gap-2 rounded-md border border-slate-100 bg-slate-50 px-2.5 py-1.5"><Search className="size-3 text-slate-300" /><div className="h-2 w-24 rounded-full bg-slate-200" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="absolute inset-4 flex flex-col gap-2 rounded-lg border border-teal-200/40 bg-white p-3 shadow-sm">{t0}<div className="flex-1 space-y-1.5"><DashboardRow best={true} /><DashboardRow /><DashboardRow /><DashboardRow /></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="animate-float-slow absolute -top-1 -right-1"><div className="flex items-center gap-1 rounded-full bg-teal-500 px-2.5 py-1 text-[10px] font-medium text-white shadow-md"><CheckCircle2 className="size-3" />Best price</div></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="animate-float-medium absolute -bottom-1 -left-1"><div className="flex items-center gap-1 rounded-full border border-teal-200 bg-white px-2.5 py-1 text-[10px] font-medium text-teal-600 shadow-sm"><TrendingDown className="size-3" />-12%</div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="relative h-44 overflow-hidden rounded-xl bg-teal-50/50 sm:h-64">{t1}{t2}{t3}<div className="animate-float-fast absolute top-2 -left-2"><div className="flex items-center gap-1 rounded-full border border-teal-200 bg-white px-2 py-1 text-[10px] text-teal-600 shadow-sm"><ShieldCheck className="size-3" /></div></div></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
}

/* ------------------------------------------------------------------ */
/*  Small reusable pieces                                              */
/* ------------------------------------------------------------------ */
function ExcelFile(t0) {
  const $ = _c(9);
  const {
    label,
    variant
  } = t0;
  const bg = variant === "gray" ? "bg-white border-slate-200" : "bg-teal-50 border-teal-200";
  const iconColor = variant === "gray" ? "text-emerald-600/40" : "text-teal-500";
  const textColor = variant === "gray" ? "text-slate-500" : "text-teal-600";
  const t1 = `flex items-center gap-1.5 rounded-lg border ${bg} px-2.5 py-1.5 shadow-sm`;
  const t2 = `size-4 ${iconColor}`;
  let t3;
  if ($[0] !== t2) {
    t3 = <FileSpreadsheet className={t2} />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = `text-[10px] font-medium ${textColor}`;
  let t5;
  if ($[2] !== label || $[3] !== t4) {
    t5 = <span className={t4}>{label}</span>;
    $[2] = label;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t1 || $[6] !== t3 || $[7] !== t5) {
    t6 = <div className={t1}>{t3}{t5}</div>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  return t6;
}
function TelegramMsg() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Send className="size-3.5 text-sky-400/60" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 shadow-sm">{t0}<div className="flex gap-1"><div className="h-1.5 w-8 rounded-full bg-slate-200" /><div className="h-1.5 w-5 rounded-full bg-slate-200" /></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function DashboardRow(t0) {
  const $ = _c(12);
  const {
    best: t1
  } = t0;
  const best = t1 === undefined ? false : t1;
  const t2 = `flex items-center gap-2 rounded-md px-2 py-1.5 ${best ? "bg-teal-50 ring-1 ring-teal-200/50" : ""}`;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="h-2 w-16 rounded-full bg-slate-200" />;
    t4 = <div className="h-2 w-10 rounded-full bg-slate-100" />;
    $[0] = t3;
    $[1] = t4;
  } else {
    t3 = $[0];
    t4 = $[1];
  }
  const t5 = `h-2 w-12 rounded-full ${best ? "bg-teal-400" : "bg-slate-200"}`;
  let t6;
  if ($[2] !== t5) {
    t6 = <div className={t5} />;
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  let t7;
  if ($[4] !== best) {
    t7 = best ? <CheckCircle2 className="size-3 text-teal-500" /> : null;
    $[4] = best;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== t6 || $[7] !== t7) {
    t8 = <div className="ml-auto flex items-center gap-1">{t6}{t7}</div>;
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t2 || $[10] !== t8) {
    t9 = <div className={t2}>{t3}{t4}{t8}</div>;
    $[9] = t2;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  return t9;
}

/* ------------------------------------------------------------------ */
/*  Main section                                                       */
/* ------------------------------------------------------------------ */
export function ProblemSection() {
  const $ = _c(68);
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] !== t) {
    t0 = t("landing.problem.pain1");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t) {
    t1 = t("landing.problem.pain2");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("landing.problem.pain3");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t) {
    t3 = t("landing.problem.pain4");
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t0 || $[9] !== t1 || $[10] !== t2 || $[11] !== t3) {
    t4 = [t0, t1, t2, t3];
    $[8] = t0;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const painPoints = t4;
  let t5;
  if ($[13] !== t) {
    t5 = t("landing.problem.gain1");
    $[13] = t;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t) {
    t6 = t("landing.problem.gain2");
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t) {
    t7 = t("landing.problem.gain3");
    $[17] = t;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t) {
    t8 = t("landing.problem.gain4");
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t5 || $[22] !== t6 || $[23] !== t7 || $[24] !== t8) {
    t9 = [t5, t6, t7, t8];
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const gains = t9;
  let t10;
  let t11;
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <FloatingIcons items={[{
      icon: FileSpreadsheet,
      className: "top-10 right-[6%]",
      color: "text-rose-300/60",
      animation: "animate-float-slow"
    }, {
      icon: Send,
      className: "bottom-20 left-[7%]",
      color: "text-sky-300/50",
      animation: "animate-float-medium"
    }, {
      icon: ArrowRight,
      className: "top-1/2 right-[3%]",
      color: "text-amber-300/50",
      animation: "animate-float-fast"
    }]} />;
    t11 = <div className="pointer-events-none absolute inset-x-0 -top-16 h-16 bg-linear-to-b from-white to-transparent" />;
    t12 = <div className="pointer-events-none absolute inset-x-0 -bottom-16 h-16 bg-linear-to-b from-transparent to-white" />;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
  } else {
    t10 = $[26];
    t11 = $[27];
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t) {
    t13 = t("landing.problem.badge");
    $[29] = t;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t13) {
    t14 = <span className="mb-4 inline-block rounded-full border border-teal-200/60 bg-teal-50/80 px-4 py-1.5 text-xs font-medium tracking-wide text-teal-700 uppercase">{t13}</span>;
    $[31] = t13;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t) {
    t15 = t("landing.problem.title");
    $[33] = t;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t15) {
    t16 = <h2 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:mt-4 sm:text-4xl">{t15}</h2>;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t14 || $[38] !== t16) {
    t17 = <div className="mb-8 text-center sm:mb-14">{t14}{t16}</div>;
    $[37] = t14;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <X className="size-3" />;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t) {
    t19 = t("landing.problem.before");
    $[41] = t;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t19) {
    t20 = <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-rose-50 px-3 py-1 text-xs font-medium text-rose-600 sm:mb-4">{t18}{t19}</div>;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <ChaosScene />;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== painPoints) {
    t22 = <ul className="mt-4 space-y-2 sm:mt-6 sm:space-y-2.5">{painPoints.map(_temp)}</ul>;
    $[46] = painPoints;
    $[47] = t22;
  } else {
    t22 = $[47];
  }
  let t23;
  if ($[48] !== t20 || $[49] !== t22) {
    t23 = <div className="rounded-2xl border border-slate-100 bg-white p-4 sm:p-8">{t20}{t21}{t22}</div>;
    $[48] = t20;
    $[49] = t22;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <MessageSquare className="size-3" />;
    $[51] = t24;
  } else {
    t24 = $[51];
  }
  let t25;
  if ($[52] !== t) {
    t25 = t("landing.problem.after");
    $[52] = t;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  let t26;
  if ($[54] !== t25) {
    t26 = <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-teal-100 px-3 py-1 text-xs font-medium text-teal-700 sm:mb-4">{t24}{t25}</div>;
    $[54] = t25;
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <OrderScene />;
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== gains) {
    t28 = <ul className="mt-4 space-y-2 sm:mt-6 sm:space-y-2.5">{gains.map(_temp2)}</ul>;
    $[57] = gains;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== t26 || $[60] !== t28) {
    t29 = <div className="rounded-2xl border border-teal-100 bg-teal-50/30 p-4 sm:p-8">{t26}{t27}{t28}</div>;
    $[59] = t26;
    $[60] = t28;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] !== t23 || $[63] !== t29) {
    t30 = <div className="grid gap-6 lg:grid-cols-2">{t23}{t29}</div>;
    $[62] = t23;
    $[63] = t29;
    $[64] = t30;
  } else {
    t30 = $[64];
  }
  let t31;
  if ($[65] !== t17 || $[66] !== t30) {
    t31 = <section className="relative overflow-hidden bg-slate-50/50 px-4 py-12 sm:px-6 sm:py-20">{t10}{t11}{t12}<div className="mx-auto max-w-7xl">{t17}{t30}</div></section>;
    $[65] = t17;
    $[66] = t30;
    $[67] = t31;
  } else {
    t31 = $[67];
  }
  return t31;
}
function _temp2(point_0) {
  return <li key={point_0} className="flex items-start gap-2.5 text-sm text-slate-600"><span className="mt-1.5 inline-block size-1.5 shrink-0 rounded-full bg-teal-500" />{point_0}</li>;
}
function _temp(point) {
  return <li key={point} className="flex items-start gap-2.5 text-sm text-slate-500"><span className="mt-1.5 inline-block size-1.5 shrink-0 rounded-full bg-rose-300" />{point}</li>;
}